import { useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import SideDrawer, {
  FieldLabel,
  SummaryRow,
  fieldSx,
  primaryButtonSx,
} from "components/SideDrawer";
import { useToast } from "components/Toast";
import useDebounce from "hooks/useDebounce";
import { Booking } from "services/manualBooking";
import {
  CancelAction,
  CancelPayload,
  cancelFlight,
  formatNaira,
  getApiError,
  previewFlightCancel,
} from "services/finance";
import BookingSummaryCard, { passengerNames } from "./BookingSummaryCard";

const EMAIL_DEFAULTS: Record<CancelAction, { subject: string; body: string }> = {
  refund: {
    subject: "Your WiaMove Refund Update",
    body: "Your refund has been fully initiated and processed to the account provided. We will notify you once the transaction has been completed.",
  },
  cancellation: {
    subject: "Your WiaMove Booking Has Been Cancelled",
    body: "Your booking has been cancelled. Please contact us if you have any questions.",
  },
};

const CancelRefundDrawer = ({
  open,
  onClose,
  booking,
}: {
  open: boolean;
  onClose: () => void;
  booking: Booking;
}) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [action, setAction] = useState<CancelAction>("refund");
  const [refundToCustomer, setRefundToCustomer] = useState("");
  const [revenueRetained, setRevenueRetained] = useState("");
  const [reverseCommission, setReverseCommission] = useState(true);
  const [emailSubject, setEmailSubject] = useState(EMAIL_DEFAULTS.refund.subject);
  const [emailBody, setEmailBody] = useState(EMAIL_DEFAULTS.refund.body);

  // Reset when the drawer opens, during render rather than in an effect, so
  // the preview never fires once with the previous session's values.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setAction("refund");
      setRefundToCustomer("");
      setRevenueRetained("");
      setReverseCommission(true);
      setEmailSubject(EMAIL_DEFAULTS.refund.subject);
      setEmailBody(EMAIL_DEFAULTS.refund.body);
    }
  }

  const handleActionChange = (next: CancelAction) => {
    // Only swap the email copy if the admin hasn't customised it.
    if (emailSubject === EMAIL_DEFAULTS[action].subject)
      setEmailSubject(EMAIL_DEFAULTS[next].subject);
    if (emailBody === EMAIL_DEFAULTS[action].body)
      setEmailBody(EMAIL_DEFAULTS[next].body);
    setAction(next);
  };

  const isRefund = action === "refund";
  const payload: CancelPayload = {
    action,
    refundToCustomer:
      isRefund && refundToCustomer !== "" ? Number(refundToCustomer) : undefined,
    revenueRetained: revenueRetained === "" ? 0 : Number(revenueRetained),
    reverseWakanowCommission: reverseCommission,
  };

  const payloadKey = JSON.stringify(payload);
  const debouncedKey = useDebounce(payloadKey, 400);
  const previewQuery = useQuery({
    queryKey: ["flightCancelPreview", booking.id, debouncedKey],
    queryFn: () => previewFlightCancel(booking.id, JSON.parse(debouncedKey)),
    // Only preview settled input, never a value that's about to be replaced.
    enabled: open && debouncedKey === payloadKey,
    // The preview depends on the booking's current server state; never
    // serve a cached preview from before a reschedule/cancel/add-on.
    staleTime: 0,
    retry: false,
    placeholderData: (prev) => prev,
  });
  const preview = previewQuery.data?.data;
  const previewErrors: string[] = [
    ...(preview?.errors || []),
    ...(previewQuery.error ? [getApiError(previewQuery.error, "")] : []),
  ].filter(Boolean);

  const mutation = useMutation({
    mutationFn: () =>
      cancelFlight(booking.id, {
        ...payload,
        emailSubject: emailSubject.trim(),
        emailBody: emailBody.trim(),
        sendEmail: true,
      }),
    onSuccess: (res) => {
      showToast(
        res?.message || (isRefund ? "Refund processed" : "Booking cancelled"),
        "success",
      );
      queryClient.invalidateQueries({ queryKey: ["flightFinance", booking.id] });
      queryClient.invalidateQueries({ queryKey: ["finance"] });
      queryClient.invalidateQueries({ queryKey: ["transactions"] });
      onClose();
    },
    onError: (error: any) =>
      showToast(getApiError(error, "Failed to process cancellation"), "error"),
  });

  const canSubmit =
    revenueRetained !== "" &&
    (!isRefund || Number(refundToCustomer) > 0) &&
    emailSubject.trim() &&
    emailBody.trim() &&
    (preview?.errors?.length || 0) === 0;

  return (
    <SideDrawer open={open} onClose={onClose} title="Cancellation / Refund">
      <BookingSummaryCard booking={booking} />

      <Typography fontSize={16} fontWeight={600} color="#101828" mb="8px">
        Cancellation / Refund
      </Typography>
      <TextField
        select
        fullWidth
        size="small"
        sx={fieldSx}
        value={action}
        onChange={(e) => handleActionChange(e.target.value as CancelAction)}
      >
        <MenuItem value="refund">Refund</MenuItem>
        <MenuItem value="cancellation">Cancellation</MenuItem>
      </TextField>
      <Typography fontSize={13} color="#667085" mt="8px">
        Process the customer's cancellation or refund and send a notification with
        the updated transaction details.
      </Typography>

      <Typography fontSize={16} fontWeight={600} color="#101828" mt="24px">
        Customer Communication
      </Typography>
      <Typography fontSize={13} color="#667085" mb="12px">
        Edit the message before sending the cancellation or refund notification.
      </Typography>
      <FieldLabel>Email Subject</FieldLabel>
      <TextField
        fullWidth
        size="small"
        sx={{ ...fieldSx, mb: "12px" }}
        value={emailSubject}
        onChange={(e) => setEmailSubject(e.target.value)}
      />
      <FieldLabel>Message Body</FieldLabel>
      <TextField
        fullWidth
        multiline
        rows={4}
        sx={fieldSx}
        value={emailBody}
        onChange={(e) => setEmailBody(e.target.value)}
      />

      <Typography fontSize={16} fontWeight={600} color="#101828" mt="24px">
        Cancellation / Refund Revenue
      </Typography>
      <Typography fontSize={13} color="#667085" mb="12px">
        Enter the final revenue generated from this cancellation or refund
        transaction.
      </Typography>

      {isRefund && (
        <Box mb="12px">
          <FieldLabel>Refund to Customer</FieldLabel>
          <TextField
            fullWidth
            size="small"
            type="number"
            sx={fieldSx}
            value={refundToCustomer}
            onChange={(e) => setRefundToCustomer(e.target.value)}
            helperText={
              preview?.amountPaid
                ? `Customer paid ${formatNaira(preview.amountPaid)}`
                : undefined
            }
            InputProps={{
              startAdornment: <InputAdornment position="start">₦</InputAdornment>,
            }}
          />
        </Box>
      )}

      <FieldLabel>Revenue Retained</FieldLabel>
      <TextField
        fullWidth
        size="small"
        type="number"
        sx={fieldSx}
        value={revenueRetained}
        onChange={(e) => setRevenueRetained(e.target.value)}
        helperText="Use a negative amount if this transaction was a loss."
        InputProps={{
          startAdornment: <InputAdornment position="start">₦</InputAdornment>,
        }}
      />

      <FormControlLabel
        sx={{ mt: "8px" }}
        control={
          <Switch
            checked={reverseCommission}
            onChange={(e) => setReverseCommission(e.target.checked)}
          />
        }
        label={
          <Typography fontSize={14} color="#101828">
            Reverse Wakanow commission
          </Typography>
        }
      />

      <Box bgcolor="#F9FAFB" borderRadius="8px" p="12px 16px" mt="16px">
        <SummaryRow label="Action" value={isRefund ? "Refund" : "Cancellation"} />
        <SummaryRow
          label="Customer"
          value={preview?.customer || passengerNames(booking)}
        />
        <SummaryRow label="PNR" value={preview?.pnr || booking.pnr || "N/A"} />
        {isRefund && (
          <SummaryRow
            label="Refund to customer"
            value={formatNaira(preview?.refundToCustomer ?? payload.refundToCustomer)}
          />
        )}
        {preview && (
          <>
            <SummaryRow
              label="Refund from Wakanow"
              value={formatNaira(preview.supplierRefund)}
            />
            <SummaryRow
              label="Commission reversed"
              value={formatNaira(preview.commissionReversed)}
            />
          </>
        )}
        <SummaryRow
          label="Revenue"
          value={formatNaira(preview?.revenue ?? payload.revenueRetained)}
          bold
        />
        {preview && (
          <SummaryRow
            label="Profit impact"
            value={formatNaira(preview.profitImpact)}
            bold
          />
        )}
      </Box>

      {previewErrors.length > 0 && (
        <Box mt="12px">
          {previewErrors.map((err, i) => (
            <Typography key={i} fontSize={13} color="#D92D20">
              {err}
            </Typography>
          ))}
        </Box>
      )}

      <Divider sx={{ my: "24px" }} />
      <Button
        fullWidth
        variant="contained"
        sx={primaryButtonSx}
        disabled={!canSubmit || mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        {mutation.isPending ? "Sending..." : "Send Notification"}
      </Button>
      <Button
        fullWidth
        onClick={onClose}
        sx={{ mt: "8px", color: "#101828", textDecoration: "underline", textTransform: "none" }}
      >
        Cancel
      </Button>
    </SideDrawer>
  );
};

export default CancelRefundDrawer;
